// Wires the shared Guru user-action recorder into the portal shell
// (gurucloudai kanban b91c17bf; ChemMasters cards 142159ee, 964045bb).
//
// Mount once in PortalLayout. It:
//   - injects the recorder script (utils/guruRecorderLoader.ts) with the
//     route template of the current page as the first screen,
//   - calls GuruRecorder.setScreen(<route template>, <pathname>) on every
//     React Router location change, so screens are '/portal/customers/:slug'
//     and never the raw URL,
//   - calls GuruRecorder.setUser({ref: <user uuid>, role}) once the auth
//     context has the person at the keyboard (realUser while an admin
//     "views as" someone else; never an email),
//   - keeps the DOM dedupe watcher running so a control that carries
//     data-action and also calls trackEvent is recorded once,
//   - records portal_leave / portal_return markers when the tab goes hidden
//     or the window loses focus (services/recorderPresence.ts), carrying
//     the surface and the object open at that moment.

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { derivePersonas } from '../utils/guruWidgetMetadata';
import { ensureGuruRecorder } from '../utils/guruRecorderLoader';
import {
  setRecorderScreen,
  setRecorderUser,
  startRecorderDomDedupe,
  stopRecorderDomDedupe,
} from '../services/guruRecorderBridge';
import { resolvePortalScreen } from '../services/portalScreens';
import { startRecorderPresence, stopRecorderPresence } from '../services/recorderPresence';

export function useGuruRecorder(): void {
  const { realUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    ensureGuruRecorder(resolvePortalScreen(window.location.pathname));
    startRecorderDomDedupe();
    startRecorderPresence();
    return () => {
      stopRecorderPresence();
      stopRecorderDomDedupe();
    };
  }, []);

  useEffect(() => {
    setRecorderScreen(resolvePortalScreen(location.pathname), location.pathname);
  }, [location.pathname]);

  const userId = realUser?.id ?? null;
  const userRole = realUser ? derivePersonas(realUser) : null;
  useEffect(() => {
    setRecorderUser(userId ? { ref: userId, role: userRole ?? 'unknown' } : null);
  }, [userId, userRole]);
}
